import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { staffingAPI } from '@/services/staffingAPI';
import { ArrowLeft, Save, DollarSign, Loader2 } from 'lucide-react';

export default function StaffingCandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cand, setCand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { (async () => {
    try { const d = await staffingAPI.getCandidate(id); setCand(d.candidate); }
    catch { navigate('/staffing/candidates'); }
  })(); }, [id, navigate]);

  if (!cand) return <div className="text-slate-500 dark:text-zinc-500 text-sm p-6 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  const pi = cand.personal_info || {};
  const setPi = (k, v) => setCand({ ...cand, personal_info: { ...pi, [k]: v } });

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const payload = {
        personal_info: cand.personal_info,
        skills: cand.skills,
        work_experience: cand.work_experience,
        education: cand.education,
        visibility_status: cand.visibility_status,
        expected_compensation_min: cand.expected_compensation_min ? parseFloat(cand.expected_compensation_min) : null,
        expected_compensation_max: cand.expected_compensation_max ? parseFloat(cand.expected_compensation_max) : null,
        expected_compensation_currency: cand.expected_compensation_currency || 'USD',
        notice_period_days: cand.notice_period_days === '' || cand.notice_period_days === undefined ? null : parseInt(cand.notice_period_days, 10),
      };
      await staffingAPI.updateCandidate(id, payload);
      setMsg('Saved');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) { setMsg('Error: ' + (e?.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  return (
    <div data-testid="staffing-candidate-detail">
      <Link to="/staffing/candidates" className="text-slate-500 dark:text-zinc-500 hover:text-slate-600 dark:hover:text-zinc-300 text-sm inline-flex items-center gap-1.5 mb-4"><ArrowLeft className="w-4 h-4" /> Back</Link>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">{pi.name || 'Candidate'}</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">{pi.email}</p>
        </div>
        <button onClick={save} disabled={saving} className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50" data-testid="save-candidate-btn">
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {msg && <div className={`px-4 py-2 rounded-lg mb-4 text-sm ${msg.startsWith('Error') ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'}`}>{msg}</div>}

      <div className="space-y-5">
        <Section title="Personal Info">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Name" value={pi.name || ''} onChange={v => setPi('name', v)} dataTestId="cand-name-input" />
            <Field label="Phone" value={pi.phone || ''} onChange={v => setPi('phone', v)} />
            <Field label="Location" value={pi.location || ''} onChange={v => setPi('location', v)} />
            <Field label="LinkedIn" value={pi.linkedin || ''} onChange={v => setPi('linkedin', v)} />
          </div>
        </Section>

        <Section title="Rate Card (hidden from candidate)" subtitle="Only staffing managers + hiring orgs see this. Candidate cannot view their own compensation.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Min" type="number" value={cand.expected_compensation_min ?? ''} onChange={v => setCand({...cand, expected_compensation_min: v})} dataTestId="rate-min-input" />
            <Field label="Max" type="number" value={cand.expected_compensation_max ?? ''} onChange={v => setCand({...cand, expected_compensation_max: v})} dataTestId="rate-max-input" />
            <div>
              <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">Currency</label>
              <select value={cand.expected_compensation_currency || 'USD'} onChange={e => setCand({...cand, expected_compensation_currency: e.target.value})} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white">
                {['USD', 'INR', 'EUR', 'GBP', 'AUD', 'SGD', 'CAD'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </Section>

        <Section title="Visibility">
          <select value={cand.visibility_status} onChange={e => setCand({...cand, visibility_status: e.target.value})} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="cand-visibility-select">
            <option value="actively_looking">Actively Looking</option>
            <option value="exploring">Exploring</option>
            <option value="not_looking">Not Looking</option>
          </select>
        </Section>

        <Section title="Notice Period (days)" subtitle="Days the candidate must serve before joining. Leave blank if unknown.">
          <input type="number" min="0" value={cand.notice_period_days ?? ''}
            onChange={e => setCand({...cand, notice_period_days: e.target.value})}
            className="max-w-xs w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
            placeholder="e.g. 30"
            data-testid="cand-notice-input" />
        </Section>

        <Section title="Skills">
          <textarea value={(cand.skills || []).join(', ')} onChange={e => setCand({...cand, skills: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} rows={2} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" placeholder="Comma-separated skills" />
        </Section>

        <Section title="Work Experience">
          {(cand.work_experience || []).map((exp, i) => (
            <div key={i} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-3 mb-2">
              <div className="grid grid-cols-2 gap-2">
                <Field label="Title" value={exp.title || ''} onChange={v => {
                  const arr = [...cand.work_experience]; arr[i] = { ...arr[i], title: v }; setCand({...cand, work_experience: arr});
                }} />
                <Field label="Company" value={exp.company || ''} onChange={v => {
                  const arr = [...cand.work_experience]; arr[i] = { ...arr[i], company: v }; setCand({...cand, work_experience: arr});
                }} />
              </div>
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-5">
      <h3 className="text-slate-900 dark:text-white text-sm font-medium mb-1">{title}</h3>
      {subtitle && <p className="text-slate-500 dark:text-zinc-500 text-xs mb-3">{subtitle}</p>}
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', dataTestId }) {
  return (
    <div>
      <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={dataTestId}
        className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" />
    </div>
  );
}
